import Axios from "axios";
import {Checkbox, Label, Stack, Text,} from "@fluentui/react";
//@ts-ignore
import ImageResize from "quill-image-resize-module-react";
import React, {useCallback, useContext, useEffect, useMemo, useState,} from "react";
import {useTranslation} from "react-i18next";
import {TitleAndContentLayout} from "./TitleAndContentLayout";
import "../styles/UserSettingsSupport.css";
//@ts-ignore
import ImageUploader from "quill-image-uploader";
import {useBoolean} from "@fluentui/react-hooks";
import {AuthContext} from "../contexts/AuthContext";
import {LoadingButton} from "./LoadingButton";
import {ReactQuillIsolated} from "../helpers/ReactQuillIsolated";
import {inlineBlobImageSources} from "../helpers/ReactQuillIsolatedHelpers";
import {SingleTenantItemForm} from "./SingleTenantItemForm";
import {TenantResource} from "../enums/TenantResource";
import {
    HeaderElement,
    ITenantFormLayout,
    NumberElement,
    PathElement,
} from "./OnixElements";
import {UserOnixDesktopSettingsDto} from "../API";

export const UserSettingsSupport = () => {
    const {t} = useTranslation();
    const {UserInfo, FetchUserInfo} = useContext(AuthContext);
    const [value, setValue] = useState<string>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const saveSignature = useCallback(async () => {
        setLoading();
        try {
            const signature = await inlineBlobImageSources(value ?? "");
            setValue(signature);
            await Axios.post("UserInfo/SetSupportSignature", {signature});
            await FetchUserInfo();
        } finally {
            setNotLoading();
        }
    }, [value, FetchUserInfo, setLoading, setNotLoading]);

    useEffect(() => {
        if (UserInfo) {
            setLoading();
            Axios.get("/UserInfo/SupportSignature")
                .then(({data}) => {
                    setValue(data);
                })
                .finally(setNotLoading);
        }
    }, [UserInfo]);

    const setSupportTimeLogsAlwaysNonBillable = useCallback(
        (change?: boolean) => {
            setLoading();
            Axios.get("/UserInfo/ToggleSupportTimeLogsAlwaysNonBillable")
                .then(() => FetchUserInfo())
                .finally(setNotLoading);
        },
        [FetchUserInfo, setLoading, setNotLoading],
    );

    const setSupportTopBarCountersOpenTicketList = useCallback(
        (change?: boolean) => {
            setLoading();
            Axios.post("/UserInfo/SupportTopBarCountersOpenTicketList", {}, {
                params: {enabled: change !== false},
            })
                .then(() => FetchUserInfo())
                .finally(setNotLoading);
        },
        [FetchUserInfo, setLoading, setNotLoading],
    );

    const userOnixDesktopSettingsForm = useMemo<
        ITenantFormLayout<UserOnixDesktopSettingsDto>
    >(
        () => ({
            supportTimeLogRoundingMinutes: {
                type: new NumberElement({}),
                translationKey: t("supportTimeLogRoundingMinutes"),
            },
            onixDesktopSettingsHeader: {
                type: new HeaderElement({title: "Postavke Onix desktop aplikacije"}),
            },
            onixDesktopPantheonExePath: {
                type: new PathElement({}),
                translationKey: t("onixDesktopPantheonExePath") + " (Windows)",
            },
            onixDesktopPantheonIniPath: {
                type: new PathElement({}),
                translationKey: t("onixDesktopPantheonIniPath") + " (Windows)",
            },
            onixDesktopPantheonExePathMac: {
                type: new PathElement({}),
                translationKey: t("onixDesktopPantheonExePath") + " (Mac)",
            },
            onixDesktopPantheonIniPathMac: {
                type: new PathElement({}),
                translationKey: t("onixDesktopPantheonIniPath") + " (Mac)",
            },
        }),
        [t],
    );

    const afterUserSettingsFormAction = useCallback(() => {
        FetchUserInfo();
    }, [FetchUserInfo]);

    return (
        <TitleAndContentLayout
            title={t("support-settings")}
            isSubtitle
            pad
            lessPadding
        >
            <Stack tokens={{childrenGap: 10}}>
                <Text variant={"large"}>{t("ticket-signature")}</Text>
                {value != undefined ? (
                    <>
                        <ReactQuillIsolated
                            backgroundColor="white"
                            originalMessageColor="lightgray"
                            cacheIdentifier={"userSettings"}
                            value={value}
                            onContentChange={(content) => setValue(content ?? "")}
                        />
                        <LoadingButton
                            loading={loading}
                            text={t("save")}
                            onClick={saveSignature}
                        />
                    </>
                ) : null}
            </Stack>
            <Label>Postavke podrške</Label>
            <Checkbox
                disabled={loading}
                onChange={(_, change) => setSupportTimeLogsAlwaysNonBillable(change)}
                label={t("supportTimeLogsAlwaysNonBillable")}
                checked={UserInfo?.user?.supportTimeLogsAlwaysNonBillable}
            />
            <Checkbox
                disabled={loading}
                onChange={(_, change) => setSupportTopBarCountersOpenTicketList(change)}
                label="Top bar brojači podrške otvaraju listu tiketa"
                checked={UserInfo?.user?.supportTopBarCountersOpenTicketList !== false}
            />
            <SingleTenantItemForm
                resource={TenantResource.UserOnixDesktopSettings}
                form={userOnixDesktopSettingsForm}
                baseDto={{}}
                query={{}}
                afterFormAction={afterUserSettingsFormAction}
            />
        </TitleAndContentLayout>
    );
};
